import React, { useLayoutEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useKT } from "../i18n/kt";
import { PRINT_CSS } from "./CostingPrint";

// Portrait A4 at 96 dpi with 14 mm top/bottom and 15 mm side margins: 180 × 269 mm of room.
// The sheet is laid out a little narrower (175 mm) and fitted to 95% of the height, so printers
// that can't print to their own edge never cut anything off.
const PAGE_MARGIN = "14mm 15mm";
const PAGE_W = 661;
const PAGE_H = Math.floor(1016 * 0.95);
// A sheet up to this much longer than one page is shrunk to fit one; longer ones run over pages.
// Never below ~74% so the smallest text stays about 8 pt.
const SHRINK_LIMIT = 1.35;

/** How much to shrink so the sheet fits one page, or 1 when it runs over pages anyway. */
export const fitScale = (contentHeight: number): number => {
  if (contentHeight <= PAGE_H) return 1;
  if (contentHeight <= PAGE_H * SHRINK_LIMIT) return Math.floor((PAGE_H / contentHeight) * 1000) / 1000;
  return 1;
};

export const pageCount = (contentHeight: number, scale: number) => Math.max(1, Math.ceil((contentHeight * scale) / PAGE_H));

/** Print just the sheet from a hidden frame, so the app around it never ends up on paper. */
const printSheet = (html: string, title: string, scale: number) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(frame);
  const doc = frame.contentWindow?.document;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title.replace(/</g, "")}</title>
<style>@page { size: A4 portrait; margin: ${PAGE_MARGIN}; } html, body { margin: 0; background: #fff; }
${PRINT_CSS} .kc-print { zoom: ${scale}; }</style></head><body>${html}</body></html>`);
  doc.close();
  const win = frame.contentWindow;
  const go = () => {
    win.focus();
    win.print();
    window.setTimeout(() => frame.remove(), 1000);
  };
  // Give the QR code and fonts a moment to lay out.
  window.setTimeout(go, 250);
};

/**
 * See the paper copy before saving it: portrait A4, shrunk to one page when it is only a little long,
 * otherwise over several pages with the column names repeated on each. "Download PDF" opens the
 * print dialog with only the sheet in it; choose "Save as PDF" there.
 */
const CostingPdfPreview = ({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
  const { t } = useKT();
  const sheet = useRef<HTMLDivElement>(null);
  const desk = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [view, setView] = useState(1);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      if (sheet.current) setHeight(sheet.current.scrollHeight);
      if (desk.current) setView(Math.min(1, (desk.current.clientWidth - 32) / (PAGE_W + 114)));
    };
    const id = window.setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const scale = fitScale(height);
  const pages = pageCount(height, scale);
  const shown = view * scale;

  return (
    <Modal isOpen={open} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>{t("PDF preview")}</ModalHeader>
      <ModalBody>
        <style>{PRINT_CSS}</style>
        <div className="kc-pdf-desk" ref={desk}>
          <div
            className="kc-pdf-page"
            style={{
              // The white paper includes its margins, so the preview shows the gap the printout will have.
              boxSizing: "content-box",
              padding: `${53 * view}px ${57 * view}px`,
              width: PAGE_W * view,
              height: Math.max(PAGE_H, height * scale) * view,
            }}
          >
            <div ref={sheet} style={{ transform: `scale(${shown})`, transformOrigin: "top left", width: PAGE_W }}>
              {children}
            </div>
            {Array.from({ length: pages - 1 }, (_, i) => (
              <div key={i} className="kc-pdf-break" style={{ top: 53 * view + (i + 1) * PAGE_H * view }}>
                <span>{t("Page {{n}}", { n: i + 2 })}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="kc-pdf-bar">
          <span className="kc-muted">
            {t("A4 portrait")} · {pages === 1 ? (scale < 1 ? t("shrunk to fit one page") : t("one page")) : t("{{n}} pages, column names on each", { n: pages })}
          </span>
          <button type="button" className="kh-btn" onClick={onClose}>
            {t("Close")}
          </button>
          <button type="button" className="kh-btn is-primary" onClick={() => sheet.current && printSheet(sheet.current.innerHTML, title, scale)}>
            {t("Download PDF")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CostingPdfPreview;
